import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReferralFeedbackLetterView, ReferralFeedbackPrintView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PrintHeader,
  PrintLines,
  PrintSheet,
  printSheet,
  TextArea,
} from '@wodi/ui';
import { useRef, useState, type Ref } from 'react';

import { ApiError } from '../api/client.ts';
import {
  feedbackPreviewQuery,
  feedbackPrintQuery,
  issueFeedbackLetter,
  markFeedbackPrinted,
  referralKeys,
} from '../api/referrals.ts';
import documentStyles from '../documents/documents.module.css';
import {
  ClinicianFooter,
  DOCUMENT_PRINT_TEXT,
  patientLines,
  PrintLoadError,
  summaryAllergies,
  SummaryPrint,
  title,
} from '../documents/prints.tsx';
import { SummaryPreview } from '../documents/referrals.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { toasts } from '../toasts.ts';
import styles from './referrals.module.css';

// Feedback letters back to the facility that referred a patient here
// (docs/plans/phase2-referrals.md §1.1 item 5; F6 TODO(clinical-verify)): the visit summary
// the server snapshots at issue, the clinician's findings and management, and
// recommendations. Printed on A4, black on white; every print is counted and audited.

export const FEEDBACK_PRINT_TEXT = {
  en: {
    feedback: 'Referral feedback letter',
    feedbackNo: 'Feedback letter no.',
    to: 'To',
    theirReference: 'Your reference',
    theirLetter: 'Your letter dated',
    referredBy: 'Referred by',
    received: 'Received here',
    findings: 'Findings and management',
    recommendations: 'Recommendations',
    thanks: 'Thank you for referring this patient. This is what we found and did.',
  },
} as const;

const F = FEEDBACK_PRINT_TEXT.en;
const T = DOCUMENT_PRINT_TEXT.en;

export function FeedbackSheet({
  print,
  sheetRef,
}: {
  print: ReferralFeedbackPrintView;
  sheetRef?: Ref<HTMLElement>;
}) {
  const l = print.letter;
  const r = print.referral;
  return (
    <PrintSheet
      paper="a4"
      label={F.feedback}
      {...(sheetRef === undefined ? {} : { ref: sheetRef })}
    >
      <PrintHeader facility={print.facility.name} title={title(F.feedback, print.copy)} />
      <PrintLines
        items={[
          { label: F.feedbackNo, value: l.feedbackNumber, identifier: true },
          { label: T.issued, value: formatInstant(l.issuedAt) },
          { label: F.to, value: l.toFacilityName },
          ...(l.toFacilityMflCode === null
            ? []
            : [{ label: T.mflCode, value: l.toFacilityMflCode, identifier: true }]),
          ...(r.otherService === null ? [] : [{ label: F.referredBy, value: r.otherService }]),
          ...(r.externalReference === null
            ? []
            : [{ label: F.theirReference, value: r.externalReference, identifier: true }]),
          ...(r.theirLetterDate === null
            ? []
            : [{ label: F.theirLetter, value: formatIsoDate(r.theirLetterDate) }]),
          { label: F.received, value: formatInstant(r.referredAt) },
          ...patientLines(print.patient, l.issuedAt),
          { label: T.allergies, value: summaryAllergies(l.summary) },
        ]}
      />
      <p className={documentStyles.printText}>{F.thanks}</p>
      <SummaryPrint summary={l.summary} wordsHeading={F.findings} />
      {l.recommendations === null ? null : (
        <>
          <h3 className={documentStyles.printHeading}>{F.recommendations}</h3>
          <p className={documentStyles.printText}>{l.recommendations}</p>
        </>
      )}
      <ClinicianFooter display={l.authorDisplay} licence={l.authorLicenceDisplay} />
    </PrintSheet>
  );
}

/** Loads the feedback letter, shows it and prints it; every print is counted and audited. */
export function FeedbackPrinter({ id, referralId }: { id: string; referralId: string }) {
  const sheet = useRef<HTMLElement>(null);
  const queryClient = useQueryClient();
  const query = useQuery(feedbackPrintQuery(id));
  const printed = useMutation({
    mutationFn: () => markFeedbackPrinted(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: referralKeys.feedbackPrint(id) });
      void queryClient.invalidateQueries({ queryKey: referralKeys.detail(referralId) });
    },
  });
  if (query.isPending) return <LoadingBlock label="Loading the feedback letter" />;
  if (query.isError)
    return <PrintLoadError error={query.error} retry={() => void query.refetch()} />;
  return (
    <div className={documentStyles.printer}>
      <div className={documentStyles.preview}>
        <FeedbackSheet print={query.data} sheetRef={sheet} />
      </div>
      {printed.isError ? (
        <Notice tone="error">
          The letter printed, but Wodi could not count the copy. Check the connection.
        </Notice>
      ) : null}
      <Button
        variant="primary"
        onPress={() => {
          if (sheet.current === null) return;
          printSheet(sheet.current);
          printed.mutate();
        }}
      >
        Print feedback letter
      </Button>
    </div>
  );
}

function FeedbackForm({
  referralId,
  onIssued,
  onCancel,
}: {
  referralId: string;
  onIssued: (letter: ReferralFeedbackLetterView) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const preview = useQuery(feedbackPreviewQuery(referralId));
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [findings, setFindings] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const results = chosen ?? new Set(preview.data?.results.map((r) => r.id) ?? []);
  const issue = useMutation({
    mutationFn: () =>
      issueFeedbackLetter(referralId, {
        clientRequestId,
        resultIds: [...results],
        clinicianText: findings,
        recommendations,
      }),
    onSuccess: (letter) => {
      toasts.show({ title: 'Feedback letter issued' });
      void queryClient.invalidateQueries({ queryKey: referralKeys.all });
      onIssued(letter);
    },
  });
  return (
    <>
      <div className={styles.fields}>
        {preview.isPending ? (
          <LoadingBlock label="Loading what the letter will carry" />
        ) : preview.isError ? (
          <Notice
            tone="error"
            action={<Button onPress={() => void preview.refetch()}>Try again</Button>}
          >
            {preview.error instanceof ApiError
              ? preview.error.message
              : 'The summary could not load. Try again.'}
          </Notice>
        ) : (
          <SummaryPreview preview={preview.data} chosen={results} onChosenChange={setChosen} />
        )}
        <TextArea
          label="Findings and management"
          description="What was found here and what was done."
          value={findings}
          onChange={setFindings}
          rows={5}
        />
        <TextArea
          label="Recommendations (optional)"
          description="Follow-up the referring facility should arrange."
          value={recommendations}
          onChange={setRecommendations}
          rows={3}
        />
        {issue.error === null ? null : (
          <p className={styles.fieldError} role="alert">
            {issue.error instanceof ApiError
              ? issue.error.message
              : "The letter couldn't be issued. Try again."}
          </p>
        )}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!preview.isSuccess}
          isPending={issue.isPending}
          pendingLabel="Issuing…"
          onPress={() => {
            issue.mutate();
          }}
        >
          Issue feedback letter
        </Button>
      </DialogActions>
    </>
  );
}

/** "Write feedback letter" and its dialog: the form, then the print preview. */
export function WriteFeedbackLetter({
  referralId,
  isDisabled,
}: {
  referralId: string;
  isDisabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [issued, setIssued] = useState<ReferralFeedbackLetterView | null>(null);
  const close = () => {
    setOpen(false);
    setIssued(null);
  };
  return (
    <>
      <Button
        isDisabled={isDisabled}
        onPress={() => {
          setOpen(true);
        }}
      >
        Write feedback letter
      </Button>
      {open ? (
        <Dialog
          title={issued === null ? 'Feedback letter' : 'Print feedback letter'}
          isOpen
          onOpenChange={(next) => {
            if (!next) close();
          }}
        >
          {issued === null ? (
            <FeedbackForm referralId={referralId} onIssued={setIssued} onCancel={close} />
          ) : (
            <>
              <FeedbackPrinter id={issued.id} referralId={referralId} />
              <DialogActions>
                <Button onPress={close}>Done</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      ) : null}
    </>
  );
}
